import { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Load Google Maps script
let isScriptLoaded = false;
let isScriptLoading = false;
const callbacks = [];

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (isScriptLoaded) {
      resolve(window.google);
      return;
    }
    
    callbacks.push({ resolve, reject });
    
    if (isScriptLoading) return;
    
    isScriptLoading = true;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      callbacks.forEach(cb => cb.resolve(window.google));
    };
    
    script.onerror = (error) => {
      isScriptLoading = false;
      callbacks.forEach(cb => cb.reject(error));
    };
    
    document.head.appendChild(script);
  });
};

export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Entrez une adresse...",
  className = ""
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => setIsLoaded(true))
      .catch(err => console.error('Failed to load Google Maps:', err));
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const options = {
      componentRestrictions: { country: 'fr' },
      fields: ['address_components', 'formatted_address', 'geometry', 'name'],
      types: ['address']
    };

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      
      if (!place.geometry) return;

      const addressData = {
        formatted_address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        address: '',
        city: '',
        zipcode: '',
      };

      // Parse address components
      place.address_components?.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) {
          addressData.address = component.long_name + ' ';
        }
        if (types.includes('route')) {
          addressData.address += component.long_name;
        }
        if (types.includes('locality')) {
          addressData.city = component.long_name;
        }
        if (types.includes('postal_code')) {
          addressData.zipcode = component.long_name;
        }
      });

      if (onSelect) {
        onSelect(addressData);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, onSelect]);

  return (
    <div className="relative">
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        className={`pl-12 h-12 rounded-xl ${className}`}
        data-testid="address-autocomplete"
      />
    </div>
  );
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  // Haversine formula
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export const formatDistance = (km) => {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
};

export default AddressAutocomplete;
